import { defineStore } from 'pinia'
import { userRequest } from '@/qingyu'
import { Storage } from '@/qingyu'

// 定义用户状态存储
export const useUserStore = defineStore('user', {
  // 状态
  state: () => ({
    // 用户信息
    userInfo: null,
    // 登录状态
    isLoggedIn: false,
    
    // 登录令牌
    token: '',
    // token过期时间
    tokenExpireTime: null,
    
    // 企业信息
    companyInfo: null,
  }),

  // 计算属性
  getters: {
    // 获取用户姓名
    userName: (state) => state.userInfo?.name || '',
    // 获取登录令牌
    getToken: (state) => state.token,
    // 是否需要强制更新账号信息
    needsForceUpdateInfo: (state) => Number(state.companyInfo?.needUpdateInfoFlag) === 1,
    // 判断token是否有效
    isTokenValid: (state) => {
      if (!state.token || !state.tokenExpireTime) return false
      const now = new Date().getTime()
      return now < state.tokenExpireTime
    }
  },
  // 动作
  actions: {
    // 用户注册
    async register(data) {
      try {
        const response = await userRequest.register(data)
        return {
          success: true,
          message: '注册成功',
          data: response
        }
      } catch (error) {
        console.error('注册失败', error)
        throw error
      }
    },
    
    // 微信登录
    async wechatLogin(payload) {
      console.log('[wechatLogin] request payload =>', payload)
      // 使用 userRequest.wechatLogin 方法调用微信登录接口
      const response = await userRequest.wechatLogin(payload);
      console.log('[wechatLogin] api response =>', response)
      // 处理登录结果
      return this.handleLoginResponse(response);
    },
    
    // 账号密码登录
    async login(data) {
      // 使用 userRequest.login 方法调用账号密码登录接口
      const response = await userRequest.login(data);
      // 处理登录结果
      return this.handleLoginResponse(response);
    },
    
    // 处理登录响应
    async handleLoginResponse(response) {
      // 直接从response中获取access_token（不使用response.data）
      this.token = response.access_token || '';

      // 设置token过期时间为response.expiration_time秒后（如果存在），否则默认为25天
      const expireSeconds = response.expiration_time || 25 * 24 * 60 * 60; // 默认25天
      const expireDate = new Date();
      expireDate.setSeconds(expireDate.getSeconds() + expireSeconds);
      this.tokenExpireTime = expireDate.getTime();

      // 保存到本地存储
      Storage.set('token', this.token);
      Storage.set('tokenExpireTime', this.tokenExpireTime);

      // 如果有token，设置登录状态为true
      if (this.token) {
        this.isLoggedIn = true;
        Storage.set('isLoggedIn', true);
      } else {
        throw new Error('登录响应中没有找到有效的token');
      }

      // 设置用户信息，登录接口已返回
      if (response.member) {
        this.userInfo = response.member;
        Storage.set('userInfo', this.userInfo);
      } else {
        throw new Error('登录响应中没有找到用户信息');
      }

      try {
        await this.getCompanyInfo();
      } catch (error) {
        console.warn('登录后获取企业信息失败', error);
      }

      return {
        success: true,
        message: '登录成功'
      };
    },

    // 初始化store
    initStore() {
      // 从本地存储恢复所有状态
      const token = Storage.get('token')
      const tokenExpireTime = Storage.get('tokenExpireTime')
      const isLoggedIn = Storage.get('isLoggedIn')
      const userInfo = Storage.get('userInfo')

      if (token) {
        this.token = token
      }

      if (tokenExpireTime) {
        this.tokenExpireTime = tokenExpireTime
      }

      if (isLoggedIn) {
        this.isLoggedIn = isLoggedIn
      }

      if (userInfo) {
        this.userInfo = userInfo
      }
    },

    // 检查token是否有效
    checkTokenValidity() {
      // 如果没有从本地存储初始化过，先尝试初始化
      if (!this.token && !this.tokenExpireTime) {
        this.initStore()
      }

      return this.isTokenValid
    },

    // 获取用户信息
    async getUserInfo() {
      // 如果已经有用户信息，直接返回
      if (this.userInfo) {
        return this.userInfo;
      }
      // 如果没有用户信息但有token，说明需要重新登录
      if (this.token) {
        throw new Error('需要重新登录');
      }
      return null;
    },
    
    // 获取企业信息
    async getCompanyInfo() {
      try {
        const response = await userRequest.getCompanyInfo();
        this.companyInfo = response
        return response
      } catch (error) {
        console.error('获取企业信息失败', error)
        return null
      }
    },
    
    // 编辑企业信息
    async editCompanyInfo(data) {
      try {
        const response = await userRequest.editCompanyInfo(data);
        if (response) {
          // 编辑成功后刷新企业信息
          await this.getCompanyInfo();
          return {
            success: true,
            message: '企业信息更新成功'
          };
        }
        return {
          success: false,
          message: '企业信息更新失败'
        };
      } catch (error) {
        console.error('编辑企业信息失败', error);
        return {
          success: false,
          message: '编辑企业信息失败: ' + (error.message || '未知错误')
        };
      }
    },
    
    // 获取企业账号列表
    async getCompanyAccountList(company_id) {
      try {
        if (!company_id) {
          return {
            success: false,
            message: '请选择企业'
          };
        }
        
        const response = await userRequest.getCompanyAccountList(company_id);
        return {
          success: true,
          data: response
        };
      } catch (error) {
        console.error('获取企业账号列表失败', error);
        return {
          success: false,
          message: '获取企业账号列表失败: ' + (error.message || '未知错误')
        };
      }
    },
    
    // 添加企业账号
    async addCompanyAccount(data) {
      try {
        const response = await userRequest.addCompanyAccount(data);
        return {
          success: true,
          message: '添加企业账号成功',
          data: response
        };
      } catch (error) {
        console.error('添加企业账号失败', error);
        return {
          success: false,
          message: '添加企业账号失败: ' + (error.message || '未知错误')
        };
      }
    },
    
    // 解绑他人企业账号
    async deleteCompanyAccount(id) {
      try {
        const response = await userRequest.deleteCompanyAccount(id);
        return {
          success: true,
          message: '解绑企业账号成功',
          data: response
        };
      } catch (error) {
        console.error('解绑企业账号失败', error);
        return {
          success: false,
          message: '解绑企业账号失败: ' + (error.message || '未知错误')
        };
      }
    },
    
    // 解绑自己的企业账号
    async deleteSelfCompanyAccount(company_id) {
      try {
        const response = await userRequest.deleteSelfCompanyAccount(company_id);
        return {
          success: true,
          message: '解绑企业账号成功',
          data: response
        };
      } catch (error) {
        console.error('解绑企业账号失败', error);
        return {
          success: false,
          message: '解绑企业账号失败: ' + (error.message || '未知错误')
        };
      }
    },

    // 设置用户信息
    setUserInfo(info) {
      this.userInfo = info
      this.isLoggedIn = true
    },
    
    // 更新用户资料
    async updateUserInfo(data) {
      try {
        const response = await userRequest.updateUserInfo(data)
        // 更新成功后刷新用户信息
        await this.getUserInfo()
        return response
      } catch (error) {
        console.error('更新用户资料失败', error)
        throw error
      }
    },

    // 更新账号/手机号/密码
    async updateSelfInfo(data) {
      try {
        const response = await userRequest.updateSelfInfo(data)

        this.userInfo = {
          ...(this.userInfo || {}),
          ...(data.username ? { username: data.username } : {}),
          ...(data.mobile ? { mobile: data.mobile, phone: data.mobile } : {})
        }
        Storage.set('userInfo', this.userInfo)

        await this.getCompanyInfo()

        return {
          success: true,
          message: '账号信息更新成功',
          data: response
        }
      } catch (error) {
        console.error('更新账号信息失败', error)
        throw error
      }
    },
    
    // 修改密码
    async changePassword(data) {
      try {
        return await userRequest.changePassword(data)
      } catch (error) {
        console.error('修改密码失败', error)
        throw error
      }
    },
    
    // 绑定手机号
    async bindPhone(data) {
      try {
        const response = await userRequest.bindPhone(data)
        // 绑定成功后刷新用户信息
        await this.getUserInfo()
        return response
      } catch (error) {
        console.error('绑定手机号失败', error)
        throw error
      }
    },
    
    // 员工绑定
    async bindEmployee(code) {
      try {
        const response = await userRequest.bindEmployee({ code })
        // 绑定成功后刷新用户信息
        await this.getUserInfo()
        return response
      } catch (error) {
        console.error('员工绑定失败', error)
        throw error
      }
    },

    // 清除用户信息（登出）
    async logout() {
      try {
        // 调用登出接口
        await userRequest.logout()
      } catch (error) {
        console.error('登出失败', error)
      } finally {
        // 无论接口是否成功，都清除本地状态
        this.clearUserInfo()
      }
    },
    
    // 清除用户状态
    clearUserInfo() {
      this.userInfo = null
      this.isLoggedIn = false
      this.token = ''
      this.tokenExpireTime = null
      this.companyInfo = null
      // 清除所有本地存储
      Storage.remove('token')
      Storage.remove('tokenExpireTime')
      Storage.remove('isLoggedIn')
      Storage.remove('userInfo')
      Storage.remove('companyInfo')
    },
    
    // 获取最新用户信息（用于在需要时更新）
    async refreshUserInfo() {
      if (this.isLoggedIn) {
        await this.getUserInfo()
        await this.getCompanyInfo()
      }
    },
  },

  // 持久化配置
  persist: {
    key: 'user-store',
    storage: localStorage,
    paths: ['userInfo', 'isLoggedIn', 'token', 'tokenExpireTime', 'companyInfo']
  }
}) 
